import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { 
  ETHERNET_PRODUCT_STRING,
  NAVIGATION_EVENTS,
  USER_EVENTS,
  masks,
  MASK,
  CLICK_EVENTS,
} from "../assets/const/eventConstants";

import { 
  getPageLoadtime,
  getVenueDetailsFromUrl,
  getBrowserAndOS, 
  getCombinedUrlSearchParams
} from "../utils/eventUtils";
import eventBus from "../utils/eventBus";
import { getGsid } from "../utils/utils";
import { eventApiLogger } from "../services/apiServices";
import transformTitleTypeAndMapUrl from "../utils/transformMapTitle";

const logEventApi = (data) => {
  const { browser, browserVersion, os } = getBrowserAndOS();
  const { subdomain, venueId, path  } = getVenueDetailsFromUrl();
  const urlSearchParams = getCombinedUrlSearchParams();
  const { title: pageName, type: pageType } = transformTitleTypeAndMapUrl();

  const eventData = {
    gsid: getGsid(),
    mac: urlSearchParams.get("mac"),
    event: "onLoad",
    browser: browser,
    browser_version: browserVersion,
    event_level: "analytics",
    event_source: "frontend",
    event_type: "page_hit",
    location: window.location.href,
    query_params: {
      ...Object.fromEntries(urlSearchParams.entries()),
    },
    on_load: getPageLoadtime(),
    os: os,
    path: path || "/",
    page_name: pageName,
    page_type: pageType,
    product: ETHERNET_PRODUCT_STRING,
    referrer: document.referrer,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    venue: venueId ?? "",
    subdomain: subdomain ?? "",
    user_agent: navigator.userAgent,
    ...data,
  };
  if (masks) {
    const masksList = masks;
    masksList.forEach((mask) => {
      if (eventData && eventData[mask]) {
        (eventData)[mask] = MASK;
      }
    });
  }
  eventApiLogger({ ...eventData });
};

// Define event handlers with their corresponding event types
const createEventHandlers = () => {
  const handlers = [
    // Signup events
    {
      eventType: USER_EVENTS.USER_SIGNUP,
      handler: (event) => {
        const customEvent = event;
        const signupData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "user_signup_success",
          username: signupData.username,
          password: signupData.password,
          venue: signupData.venue_id,
        });
      },
    },
    {
      eventType: USER_EVENTS.USER_SIGNUP_ERROR,
      handler: (event) => {
        const customEvent = event;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "user_signup_error",
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    // Click events
    {
      eventType: CLICK_EVENTS.BTN_CLICK,
      handler: (event) => {
        const customEvent = event;
        const eventData = customEvent.detail;

        logEventApi({
          event_type: "click",
          event_level: "analytics",
          event_details: "btn_click",
          label: eventData.label || "",
          venue: eventData.venue_id,
        });
      },
    },
    // Add more event handlers here as needed
    // Example:
    // {
    //   eventType: USER_EVENTS.USER_LOGOUT,
    //   handler: (event: Event) => {
    //     const customEvent = event as CustomEvent;
    //     logEventApi({
    //       event_type: "auth",
    //       event_level: "analytics",
    //       event_details: "user_logout",
    //     });
    //   }
    // },
  ];
  return handlers;
};

const EventAPI = () => {
  const location = useLocation();
  const previousPathRef = useRef(null);

  // Listen for all defined events
  useEffect(() => {
    const eventHandlers = createEventHandlers();

    // Register all event handlers
    eventHandlers.forEach(({ eventType, handler }) => {
      eventBus.on(eventType, handler);
    });

    // Clean up all event handlers
    return () => {
      eventHandlers.forEach(({ eventType, handler }) => {
        eventBus.remove(eventType, handler);
      });
    };
  }, []);

  // Simplified route change and initial load tracking
  useEffect(() => {
    const { path } = getVenueDetailsFromUrl();

    // A timeout is used to ensure that other side effects, such as `react-helmet`
    // updating the document title, have completed before we log the event.
    const timerId = setTimeout(() => {
      // Log initial page load if this is the first render
      if (previousPathRef.current === null) {
        logEventApi({
          product: ETHERNET_PRODUCT_STRING,
        });
      }
      // Log route change if path has changed
      else if (previousPathRef.current !== path) {
        logEventApi({
          event: "routeChange",
          event_type: NAVIGATION_EVENTS.ROUTE_CHANGE,
          previous_path: previousPathRef.current || "/",
          product: ETHERNET_PRODUCT_STRING,
        });
      }
      // Update the previous path reference after logging
      previousPathRef.current = path;
    }, 0);

    return () => clearTimeout(timerId);
  }, [location]);

  // Click Handlers
  /**
   * Resolve a human-readable label for a clicked button. Priority:
   *   1. data-track-label (explicit override)
   *   2. aria-label
   *   3. textContent (collapsed whitespace, capped at 120 chars)
   *   4. title attribute
   *   5. name attribute
   */
  const resolveLabel = (el) => {
    const explicit = el.getAttribute("data-track-label");
    if (explicit) return explicit;
    const aria = el.getAttribute("aria-label");
    if (aria) return aria;
    const text = el.textContent?.trim().replace(/\s+/g, " ");
    if (text) return text.slice(0, 120);
    return el.getAttribute("title") ?? el.getAttribute("name") ?? undefined;
  };

  /**
   * Mount a single global, capture-phase click listener that detects clicks on
   * any `<button>` or `role="button"` element anywhere in the app and forwards
   * them to /log/event via eventBus CLICK_EVENTS. Component authors do not need to wrap
   * or instrument individual buttons.
   *
   * Conventions:
   *   - data-track-label="Custom Label"  override the resolved label
   *   - data-track-ignore="true"         skip logging for this button entirely
   */
  useEffect(() => {
    const handler = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const btn = target.closest(
        'button, [role="button"], a[href], [role="link"]',
      );
      if (!btn) return;
      if (btn.dataset.trackIgnore === "true") return;
      if (btn.getAttribute("aria-disabled") === "true") return;
      const label = resolveLabel(btn);
      if (!label) return;
      logEventApi({
        event_details: "btn_click",
        event_type: "click" ,
        event_level: "analytics" ,
        label,
      });
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
};;

export default EventAPI;
